import { useState, useEffect, useCallback } from 'react';

interface UseInfiniteScrollOptions<T> {
  fetchData: (page: number) => Promise<T[]>;
  loadingElementRef: React.RefObject<HTMLElement | null>;
  initialPage?: number;
  initialData?: T[]; // Optional: Initial data to populate the `data` state
  maxRetries?: number;
  onSuccess?: (data: T[]) => void;
  onError?: (error: string) => void;
  hasMoreExternal?: boolean;
}

const useInfiniteScroll = <T>({
  fetchData,
  loadingElementRef,
  initialPage = 1,
  initialData = [],
  maxRetries = 0,
  onSuccess,
  onError,
  hasMoreExternal,
}: UseInfiniteScrollOptions<T>) => {
  const [data, setData] = useState<T[]>(initialData);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setPage(initialPage);
  }, [initialPage]);

  const hasMoreToUse: boolean =
    hasMoreExternal !== undefined ? hasMoreExternal : hasMore;

  const loadMoreData = useCallback(async () => {
    if (loading || !hasMoreToUse) return;

    setLoading(true);
    setError(null);

    try {
      const newData = await fetchData(page);

      if (newData.length === 0) {
        setHasMore(false);
      } else {
        setData(prevData => {
          const updatedData = [...prevData, ...newData]; // Combine old and new data
          return updatedData;
        });
        setPage(prevPage => prevPage + 1);
        setRetryCount(0); // Reset retry count on success
        onSuccess?.(newData); // Notify parent component with updated data
      }
    } catch (err) {
      if (retryCount < maxRetries) {
        setRetryCount(prevCount => prevCount + 1);
      } else {
        setError('Failed to fetch data. Please try again later.');
        setHasMore(false); // Stop further attempts
        onError?.(err as string); // Notify parent component of error
      }
    } finally {
      setLoading(false);
    }
  }, [
    page,
    loading,
    hasMoreToUse,
    retryCount,
    fetchData,
    maxRetries,
    onSuccess,
    onError,
  ]);

  // Function to reset the error state
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // Function to reset the hasMore state
  const resetHasMore = useCallback(() => {
    setHasMore(true);
  }, []);

  // Function to reset the whole  config
  const resetConfigs = useCallback(() => {
    setData(initialData);
    setPage(initialPage);
    setLoading(false);
    setHasMore(true);
    setError(null);
    setRetryCount(0);
  }, []);

  // Function to set the hasMore state to false
  const setHasMoreFalse = useCallback(() => {
    setHasMore(false);
  }, []);

  useEffect(() => {
    if (!hasMoreToUse) return;

    const observer = new IntersectionObserver(
      entries => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && !loading) {
          loadMoreData();
        }
      },
      { threshold: 1.0 }
    );

    if (loadingElementRef.current) {
      observer.observe(loadingElementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMoreData, hasMoreToUse, loading, loadingElementRef]);

  return {
    data,
    loading,
    hasError: error && error.length > 0,
    hasMore: hasMoreToUse,
    resetError,
    resetHasMore,
    resetConfigs,
    setHasMoreFalse,
  };
};

export default useInfiniteScroll;
